import { useAppLocation } from 'app-layered-layout/useAppLocation'
import { usePageActivated } from 'app-layered-layout/usePageActivated'
import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { getVisitorId } from 'redux/saga/user'
import {
  selectIsViewedAdvertisement,
  selectIsShowingLaunchSplash,
  selectIsShowingMaintenancePage,
  selectIsShowingFullPageError,
} from 'redux/selector/app'
import { UAParser } from 'ua-parser-js'
import { initializeFaro, type BeforeSendHook } from '@grafana/faro-web-sdk'
import { getDebugInfo } from 'utils/debugUtil'
import { getAPI } from 'utils/settings'

let resolve: () => void
const analyticsReadyPromise = new Promise<void>((resolve_) => {
  resolve = resolve_
})

const FIRST_LOAD_TIME_KEY = 'flt'
const NEED_REPORT_ROUTE_LIST = [
  {
    route: '/game-deposit',
    title: '頁面 - 存款',
  },
  {
    route: '/game',
    title: 'GameLobby',
  },
]

const getFirstLoadTime = (): Date => {
  const t = localStorage.getItem(FIRST_LOAD_TIME_KEY)
  if (t) {
    return new Date(JSON.parse(t))
  }

  const now = Date.now()

  localStorage.setItem(FIRST_LOAD_TIME_KEY, JSON.stringify(now))

  return new Date(now)
}

const getBasicInfo = () => {
  const parser = new UAParser(navigator.userAgent)
  const parserResults = parser.getResult()

  return {
    deviceId: getVisitorId(),
    first_open_time: getFirstLoadTime(),
    location: '',
    device_type: parserResults.device.type,
    operating_system: parserResults.os,
    app_version: process.env.REACT_APP_VERSION,
    last_active_time: new Date(),
    phone_model: parserResults.device.model,
    screen_resolution: window.screen?.orientation?.type.startsWith('landscape-')
      ? `${window.screen?.height ?? window.innerHeight}x${window.screen?.width ?? window.innerWidth}`
      : `${window.screen?.width ?? window.innerWidth}x${window.screen?.height ?? window.innerHeight}`,
    network_type: '',
    carrier: '',
  } as const
}

export interface LogEntry {
  SerialNumber?: number
  Event?: string
  Trigger?: string
  Parameters?: string
  Value?: string
  category?: string
  action?: string
  name?: string
  values?: any
  /** Matomo Action 級自訂維度（key＝dimension id，value＝字串）。空值請省略不帶。 */
  dimensions?: Record<number, string>
  [key: string]: any
}

interface ReportSystem {
  report: (log: LogEntry, basicInfo: ReturnType<typeof getBasicInfo>) => void
  setUser: (user: string, inviteCode?: any, payer?: any) => void
  onReady: (location?: any) => void
  recordPV: (location: any) => void
  onRouteChange?: (route: string, title: string) => void
}

/**
 * @type {Promise<ReportSystem>[]}
 */
const loadingReportSystems: Promise<ReportSystem>[] = []

const addReportSystem = (reportSystemPromise: Promise<ReportSystem>): void => {
  loadingReportSystems.push(
    Promise.resolve(reportSystemPromise).catch((err) => {
      console.error(err)
      return {
        report: () => {},
        setUser: () => {},
        onReady: () => {},
        recordPV: () => {},
      }
    })
  )
}
let sentOnce = false
export const initFaro = () => {
  console.log('init faro')
  if (process.env.REACT_APP_FARO_URL) {
    const beforeSend: BeforeSendHook = (item) => {
      console.log(item)
      if (typeof item.payload !== 'object' || item.payload == null || !('name' in item.payload)) {
        return null
      }
      if (item.payload.name !== 'faro.performance.resource') return null
      if (sentOnce) return null
      sentOnce = true
      return item
    }

    initializeFaro({
      url: process.env.REACT_APP_FARO_URL,
      app: {
        name: 'h5',
        namespace: process.env.REACT_APP_FARO_SITE_NAME,
        version: process.env.REACT_APP_VERSION,
        environment: process.env.REACT_APP_FARO_ENV,
      },
      // ✅ 第一筆放行，後面全部丟掉
      beforeSend,
    })
  }
}

export const setAnalyticUser = (user: string, inviteCode?: any, payer?: any): void => {
  if (process.env.NODE_ENV === 'development') {
    console.log('[analytics] set user ' + user)
  }
  loadingReportSystems.forEach((p) =>
    p.then((sys) => {
      sys.setUser(user, inviteCode, payer)
    })
  )
}

export const report = (log: LogEntry): void => {
  if (process.env.NODE_ENV === 'development' || getDebugInfo().includes('EVENT_LOG')) {
    console.groupCollapsed(`[analytics] report ${log.category} ${log.action} ${log.name}: ${log.values}`)
    console.log(log)
    console.groupEnd()
  }

  const extra = getBasicInfo()

  // Wait for analytics to be ready before sending events
  analyticsReadyPromise.then(() => {
    loadingReportSystems.forEach((p) =>
      p.then((sys) => {
        sys.report(log, extra)
      })
    )
  })
}

/** N1-5268：影片點擊埋點的 Matomo Action 級自訂維度編號（後台若不同時，改此二常數即可） */
const VIDEO_ACTOR_DIMENSION = 8
const VIDEO_TAGS_DIMENSION = 9

interface VideoClickPayload {
  /** 影片類別（1:長視頻→Video，2:短視頻→ShortVideo） */
  type?: number
  videoCode?: string
  title?: string
  /** 演員名稱清單（多位以逗號分隔上報） */
  actorNames?: string[]
  /** 分類標籤名稱清單（多個以逗號分隔上報） */
  tagNames?: string[]
}

/**
 * N1-5268：影片點擊事件上報。
 * 集中 spec 邏輯：Category 依長短、Event Name＝`{video_code}_{title}`（缺 code 退化為 title）、
 * dimension8=演員、dimension9=分類，多值以半形逗號分隔、空值省略。
 */
export const reportVideoClick = (p: VideoClickPayload): void => {
  const category = p.type === 2 ? 'ShortVideo' : 'Video'
  const name = [p.videoCode, p.title].map((s) => s?.trim()).filter(Boolean).join('_')
  const actorStr = (p.actorNames ?? []).map((s) => s?.trim()).filter(Boolean).join(',')
  const tagStr = (p.tagNames ?? []).map((s) => s?.trim()).filter(Boolean).join(',')
  const dimensions: Record<number, string> = {}
  if (actorStr) dimensions[VIDEO_ACTOR_DIMENSION] = actorStr
  if (tagStr) dimensions[VIDEO_TAGS_DIMENSION] = tagStr
  report({ category, action: 'Click_Video', name, dimensions })
}

export const useReportAppScreen = (log: LogEntry): void => {
  const isViewedAdvertisement = useSelector(selectIsViewedAdvertisement)
  const logged = useRef(false)
  const active = usePageActivated()
  useEffect(() => {
    if (isViewedAdvertisement) {
      if (!active) {
        logged.current = false
      } else if (!logged.current) {
        logged.current = true
        report({
          //   SerialNumber: 1,
          Event: 'app_screen',
          // Trigger: '進入首頁',
          // Parameters: 'page_title',
          // Value: 'cms',
          ...log,
        })
      }
    }
  })
}

const flatten = (obj: any): [string, any][] => {
  const list: [string, any][] = []
  if (obj == null || typeof obj !== 'object') {
    return list
  }
  for (let [k, v] of Object.entries(obj)) {
    if (v != null) {
      if (typeof v === 'object') {
        const subList = flatten(v)
        list.push(...subList.map(([subK, v]): [string, any] => [`${k}.${subK}`, v]))
      } else {
        list.push([k, v])
      }
    }
  }
  return list
}

export const recordPV = async (location: any): Promise<void> => {
  if (process.env.NODE_ENV === 'development') {
    console.log('[analytics] recordPV ' + window.location.href, location, flatten({ state: location.state }))
  }

  await getAnalyticReady()
  // record new location here
  loadingReportSystems.forEach((p) =>
    p.then((sys) => {
      sys.recordPV(location)
    })
  )
}

export const setAnalyticRoute = (route: string, title: string): void => {
  if (process.env.NODE_ENV === 'development') {
    console.log('[analytics] set route ' + route)
  }
  getAnalyticReady().then(() => {
    loadingReportSystems.forEach((p) =>
      p.then((sys) => {
        sys.onRouteChange?.(route, title)
      })
    )
  })
}

export const useAnalyticsPV = (): void => {
  // record page view here
  const location = useAppLocation()
  const lastRecorded = useRef<string>()

  useEffect(() => {
    if (lastRecorded.current === location.pathname + location.search + location.hash) {
      return
    }

    lastRecorded.current = location.pathname + location.search + location.hash
    recordPV(location)
  }, [location])
}

export const getAnalyticReady = (): Promise<void> => {
  return analyticsReadyPromise
}

export const setAnalyticReady = (): void => {
  if (process.env.NODE_ENV === 'development') {
    console.log('[analytics] ready')
  }
  resolve()
  loadingReportSystems.forEach((p) =>
    p.then((sys) => {
      sys.onReady()
    })
  )
}

export const useRouteLogging = (): void => {
  const location = useAppLocation()
  const pathname = location.pathname
  const prevUrl = useRef<string>()

  useEffect(() => {
    if (prevUrl.current !== pathname) {
      const route = NEED_REPORT_ROUTE_LIST.find((r) => pathname === r.route)
      if (route) {
        if (process.env.NODE_ENV === 'development') {
          console.log('route', route)
        }
        setAnalyticRoute(route.route, route.title)
      }
    }
    prevUrl.current = pathname
  }, [pathname])
}

// N1-4566 removed speed test
// function reportSpeedTest(url, seriesId, status, ms) {
//   if (
//     process.env.REACT_APP_MATOMO_HOST &&
//     process.env.REACT_APP_MATOMO_ENV_SITE_ID
//   ) {
//     // eslint-disable-next-line @typescript-eslint/no-explicit-any
//     const _paq = window._paq || []
//     _paq.push(['setCustomDimension', 3, url]) // CD3: URL
//     _paq.push(['setCustomDimension', 4, seriesId]) // CD4: series_id
//     _paq.push(['setCustomDimension', 5, status]) // CD5: status ("200"/"timeout"/"network")
//     _paq.push(['setCustomDimension', 6, String(ms)]) // CD6: ms（毫秒）

//     _paq.push(['trackEvent', 'api_speedtest', 'probe', url, ms])

//     _paq.push(['deleteCustomDimension', 3])
//     _paq.push(['deleteCustomDimension', 4])
//     _paq.push(['deleteCustomDimension', 5])
//     _paq.push(['deleteCustomDimension', 6])
//   }
// }

/**
 * @param {null|{ domain: string, time: number, code: number, type: 'result' | 'network' | 'timeout' }[]} results
 */
// N1-4566 removed speed test
// export function reportSpeedTests(results) {
//   if (results == null) {
//     console.error('[analytics] reportSpeedTests error, results is null')
//     return
//   }

//   if (process.env.NODE_ENV === 'development' || getDebugInfo().includes('EVENT_LOG')) {
//     console.groupCollapsed(`[analytics] reportSpeedTests items: ${results.length}`)
//     console.log(results)
//     console.groupEnd()
//   }

//   // prefix with H- to make it easier to identify with other environments
//   const seriesId = 'H-' + v4()

//   for (const result of results) {
//     reportSpeedTest(
//       `https://${result.domain}/sw.js`,
//       seriesId,
//       result.type === 'result' ? result.code : result.type,
//       result.time
//     )
//   }
// }

/**
 * @returns {Promise<null|{ domain: string, time: number, code: number, type: 'result' | 'network' | 'timeout' }[]>}
 */
// N1-4566 removed speed test
// export async function getSpeedTestResults() {
//   if (navigator.serviceWorker?.controller) {
//     return new Promise((resolve) => {
//       try {
//         const sw = navigator.serviceWorker
//         const handler = (ev) => {
//           if (ev.data && typeof ev.data === 'object' && ev.data.type === 'test-result') {
//             sw.removeEventListener('message', handler)
//             resolve(ev.data.data)
//           }
//         }
//         sw.addEventListener('message', handler)
//         sw.controller.postMessage('get-test-result')
//       } catch (error) {
//         console.error('[analytics] getSpeedTestResults error', error)
//         return resolve(null)
//       }
//     })
//   } else {
//     console.error('[analytics] getSpeedTestResults error, no controller')
//     return null
//   }
// }

if (process.env.REACT_APP_MATOMO_HOST && process.env.REACT_APP_MATOMO_ENV_SITE_ID) {
  addReportSystem(
    Promise.resolve({
      setUser: (user: string, inviteCode: any, payer: any) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const _paq = (window as any)._paq || []
        // Hard to read with ID, so use account instead
        _paq.push(['setUserId', user])
        // 依照用戶開啟裝置帶入 osType。1：H5 2：Android 3：iOS
        const osType = 1
        _paq.push(['setCustomDimension', 1, osType])
        // 帶入 App 版本
        _paq.push(['setCustomDimension', 2, process.env.REACT_APP_VERSION])
        // 帶入裝置 ID
        if (payer === 1) {
          _paq.push(['setCustomDimension', 3, payer])
        }
        // 代理商 ID
        _paq.push(['setCustomDimension', 4, inviteCode])
        // _paq.push(['setCustomDimension', 5, user])
      },
      report: async (log: LogEntry, _basicInfo: ReturnType<typeof getBasicInfo>) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const _paq = (window as any)._paq || []
        // Action 級 Custom Dimension：trackEvent 前設定、後清除，避免污染後續事件（比照 speed test 範例）
        const dimensions = log.dimensions
        if (dimensions) {
          for (const [id, val] of Object.entries(dimensions)) {
            if (val != null && val !== '') _paq.push(['setCustomDimension', Number(id), val])
          }
        }
        _paq.push(['trackEvent', log.category, log.action, log.name, ...(log.values != null ? [log.values] : [])])
        if (dimensions) {
          // 僅清除前面真正 set 過的維度（與上方非空檢查對稱），避免誤刪未設定的維度
          for (const [id, val] of Object.entries(dimensions)) {
            if (val != null && val !== '') _paq.push(['deleteCustomDimension', Number(id)])
          }
        }
      },
      onReady: () => {
        // markReady();
        const _paq = (window as any)._paq || []
        _paq.push(['trackPageView'])
        _paq.push(['enableLinkTracking'])
      },
      recordPV: (_location: any) => {},
      onRouteChange: (route: string, title: string) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const _paq = (window as any)._paq || []
        _paq.push(['setCustomUrl', route])
        _paq.push(['setDocumentTitle', title])
        _paq.push(['trackPageView'])
      },
    })
  )
}

/* ==========================================================================
 * N1-5226：啟動頁「進入」按鈕 → 首頁呈現耗時埋點
 *
 * - T1 由 Timer 真實點擊時 markEntryStart() 寫入 sessionStorage（bootId-tagged）。
 * - T2 由 useEntryLoadReport 於首頁露出時讀取、計算耗時並 reportEntryLoad() 上報。
 * bootId 確保 T1／T2 屬同一次 page lifecycle：reload 後模組重載即產生新 bootId，
 * 舊殘值會被 readEntryStart() 拒絕，避免跨 reload 誤報（守住「只算真實點擊」）。
 * ========================================================================== */

export const ENTRY_START_TIME_KEY = 'entry_start_time'

// sanity cap：僅攔截明顯異常殘值；放寬至 60min 以免丟棄弱網／背景切換的真實長尾點擊
// （bootId 已完整防堵跨 reload 殘留，maxAge 只作異常防呆）。
const MAX_ENTRY_AGE_MS = 60 * 60 * 1000

// 本次 page lifecycle 常數：reload 後模組重載即產生新值。
const BOOT_ID = `${Date.now()}_${Math.random().toString(16).slice(2)}`

interface EntryStartRecord {
  startedAt: number
  bootId: string
}

/**
 * T1：記錄使用者「首次」點擊「進入」的時間戳（僅本次 lifecycle 有效）。
 *
 * 冪等：本次 lifecycle 已有有效 T1 時不覆蓋——弱網下按鈕在「全力冲刺中」等待期間仍可點擊，
 * 重複點擊不應把 T1 改成較晚的時間而低估載入耗時（正是本指標最該量到的慢速情境）。
 */
export function markEntryStart(): void {
  if (readEntryStart() != null) return // 保留本次 lifecycle 首次點擊的意圖時間
  const record: EntryStartRecord = { startedAt: Date.now(), bootId: BOOT_ID }
  try {
    sessionStorage.setItem(ENTRY_START_TIME_KEY, JSON.stringify(record))
  } catch {
    // sessionStorage 不可用（無痕／隱私模式等）時忽略。
  }
}

/** 清除 T1。 */
export function clearEntryStart(): void {
  try {
    sessionStorage.removeItem(ENTRY_START_TIME_KEY)
  } catch {
    // ignore
  }
}

/**
 * 讀取 T1（startedAt）。以下任一情況回 null 並順手清除殘值：
 * 無值／解析失敗、bootId 不符（跨 reload 殘留）、startedAt 非有限數、超過 MAX_ENTRY_AGE_MS。
 */
export function readEntryStart(): number | null {
  let raw: string | null = null
  try {
    raw = sessionStorage.getItem(ENTRY_START_TIME_KEY)
  } catch {
    return null
  }
  if (raw == null) return null

  try {
    const parsed = JSON.parse(raw) as Partial<EntryStartRecord>
    const startedAt = parsed?.startedAt
    if (
      parsed?.bootId !== BOOT_ID ||
      typeof startedAt !== 'number' ||
      !Number.isFinite(startedAt) ||
      Date.now() - startedAt > MAX_ENTRY_AGE_MS
    ) {
      clearEntryStart()
      return null
    }
    return startedAt
  } catch {
    clearEntryStart()
    return null
  }
}

interface SpeedReportMessage {
  type: 'speed-report'
  data: { selectedDomain?: string | null } | null
}

/**
 * 透過 service worker 的 get-speed-report 取得「啟動時選定的 API 線路」（bare host）。
 * 無 controller 或逾時（預設 1.5s）即 resolve null；listener／timer 皆會清理避免洩漏。
 */
function getSwSelectedDomain(timeoutMs = 1500): Promise<string | null> {
  const swContainer = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined
  const controller = swContainer?.controller
  if (!swContainer || !controller) {
    return Promise.resolve(null)
  }

  return new Promise<string | null>((resolve) => {
    let settled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const handler = (ev: MessageEvent) => {
      const data = ev.data as SpeedReportMessage | undefined
      if (data && typeof data === 'object' && data.type === 'speed-report') {
        finish(data.data?.selectedDomain ?? null)
      }
    }

    const finish = (value: string | null) => {
      if (settled) return
      settled = true
      swContainer.removeEventListener('message', handler)
      if (timer != null) clearTimeout(timer)
      resolve(value)
    }

    swContainer.addEventListener('message', handler)
    timer = setTimeout(() => finish(null), timeoutMs)
    try {
      controller.postMessage('get-speed-report')
    } catch {
      finish(null)
    }
  })
}

/** SW 未提供線路時的 fallback：設定的 API base host（比照 api/config 以 origin 解析相對路徑）。 */
function fallbackApiHost(): string {
  try {
    return new URL(getAPI(), window.location.origin).host
  } catch {
    return window.location.host
  }
}

/**
 * T2：上報 Enter_Load 事件。
 * elapsedMs 必須在 T2 當下算好再傳入（避免下方 SW 非同步往返膨脹數值）。
 */
export async function reportEntryLoad(elapsedMs: number): Promise<void> {
  const selected = await getSwSelectedDomain()
  const name = selected ?? fallbackApiHost()
  report({
    category: 'Enter_Load',
    action: 'H5',
    name,
    values: elapsedMs,
  })
}

/**
 * 於 App 頂層呼叫（page-agnostic）。量測「點擊進入 (T1) → 首頁呈現 (T2)」耗時並上報。
 *
 * T2 口徑：入站 launch overlay 已移除（isViewedAdvertisement）且非讀取畫面
 * （!isShowingLaunchSplash）、非全頁阻擋態（維護頁／全頁錯誤）。此時首頁畫面已呈現。
 * 底部 config-update 等進入後提示不納入 gate。無點擊的自動進入（無廣告／跳過）無 T1 → 不上報。
 */
export function useEntryLoadReport(): void {
  const isViewedAd = useSelector(selectIsViewedAdvertisement)
  const showSplash = useSelector(selectIsShowingLaunchSplash)
  const showMaintenance = useSelector(selectIsShowingMaintenancePage)
  const showFullPageError = useSelector(selectIsShowingFullPageError)
  const reportedRef = useRef(false)

  useEffect(() => {
    if (reportedRef.current) return

    const homeVisible = isViewedAd && !showSplash && !showMaintenance && !showFullPageError
    if (!homeVisible) return

    const startedAt = readEntryStart()
    if (startedAt == null) return // 非本次真實點擊進入 / 跨 reload 殘留已濾除

    const elapsed = Date.now() - startedAt // ★ 先在 T2 當下算好，避免 SW 往返膨脹數值
    clearEntryStart()
    reportedRef.current = true
    if (elapsed < 0) return // 防呆：理論上不會發生

    void reportEntryLoad(elapsed).catch(() => {
      // 埋點失敗不影響使用者流程；靜默即可（避免潛在的 unhandled rejection）。
    })
  }, [isViewedAd, showSplash, showMaintenance, showFullPageError])
}
